import { FormValues } from '../components/form/types';
import {
    RemoveResponse,
    MultipleRemoveResponse,
    NetworkDetailResponse,
    NetworkListResponse,
    PartnerListResponse,
} from './types';
import {
    PartnerDetailResponse,
    PartnerRefundListResponse,
} from './types/partner';
import { ApiFactoryClassType } from './factory';

const partner = (factory: ApiFactoryClassType) => ({
    getNetworkList: async (
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: string,
    ): Promise<NetworkListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/network/list?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getNetworkById: async (
        id: string,
        decrypt: boolean = false,
    ): Promise<NetworkDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/network/${id}?decrypt=${decrypt ? '1' : '0'}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createNetwork: async (networkObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/network/create', factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(networkObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updateNetwork: async (id: string, networkObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/network/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(networkObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deleteNetwork: async (
        id: string,
        removePartner: boolean = false,
    ): Promise<RemoveResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/network/${id}?removePartner=${removePartner ? '1' : '0'}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deleteNetworksById: async (
        ids: Array<string>,
        removePartner: boolean = false,
    ): Promise<MultipleRemoveResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const body = [];
            for (const id of ids) {
                body.push({
                    id,
                    deletePartner: removePartner,
                });
            }

            const response = await fetch(
                new URL('/api/network/delete', factory.getApiHost()),
                {
                    method: 'DELETE',
                    body: JSON.stringify(body),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPartnerList: async (
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: string,
    ): Promise<PartnerListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/partner/list?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPartnerById: async (
        id: string,
    ): Promise<PartnerDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/partner/${id}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getRefundListByPartner: async (
        partnerId: string,
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: string,
    ): Promise<PartnerRefundListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/partner/${partnerId}/refunds?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getHintListByPartner: async (
        partnerId: string,
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: string,
    ): Promise<PartnerListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/partner/${partnerId}/hints?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPartnerByNetwork: async (
        networkId: string,
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: 'asc' | 'desc',
    ): Promise<PartnerListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/network/${networkId}/partner?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createPartner: async (partnerObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/partner/create', factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(partnerObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                        'Accept-Language': factory.getRequestLocale(),
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updatePartner: async (id: string, partnerObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/partner/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(partnerObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                        'Accept-Language': factory.getRequestLocale(),
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deletePartner: async (id: string): Promise<RemoveResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/partner/${id}`, factory.getApiHost()),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },

    deletePartnerById: async (
        ids: Array<string>,
    ): Promise<MultipleRemoveResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }

        const body = [];
        for (const id of ids) {
            body.push({
                id,
            });
        }

        const response = await fetch(
            new URL('/api/partner/delete', factory.getApiHost()),
            {
                method: 'DELETE',
                body: JSON.stringify(body),
                headers: {
                    Authorization: 'Bearer ' + session.auth.access_token,
                    'Content-type': 'application/json',
                },
                mode: 'cors',
            },
        );

        if (!response.ok) {
            factory.onError(response);
        }

        if (response.status >= 400) {
            return null;
        }

        return response.json();
    },
});

export default partner;
